import type { paths } from './schema';

/**
 * Structured API failure. Phase 7 gives the backend a typed error envelope
 * with a machine-readable `code`; until then `code` is undefined and callers
 * fall back to `status`. Views must branch on `code`/`status`, never on the
 * human-readable message.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly detail: string,
    readonly code?: string,
    readonly context?: Record<string, unknown>,
  ) {
    super(detail || `Request failed (${status})`);
    this.name = 'ApiError';
  }

  /** Distinguishes "backend unreachable" from "backend said no". */
  get isNetworkFailure(): boolean {
    return this.status === 0;
  }
}

type Query = Record<string, string | number | boolean | undefined | null>;

function buildUrl(path: string, query?: Query): string {
  const url = new URL(path, window.location.origin);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== null) {
        url.searchParams.set(key, String(value));
      }
    }
  }
  return url.toString();
}

/**
 * Single entry point for every backend read.
 */
export async function apiGet<T>(
  path: string,
  query?: Query,
  signal?: AbortSignal,
): Promise<T> {
  let response: Response;
  try {
    const init: RequestInit = {
      headers: { Accept: 'application/json' },
      ...(signal ? { signal } : {}),
    };
    response = await fetch(buildUrl(path, query), init);
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw new ApiError(0, 'Could not reach the server. Is it running?');
  }

  if (!response.ok) {
    const body: unknown = await response.json().catch(() => ({}));
    const detail =
      typeof body === 'object' && body !== null && 'detail' in body
        ? String(body.detail)
        : `Request failed (${response.status})`;
    const code =
      typeof body === 'object' && body !== null && 'code' in body
        ? String(body.code)
        : undefined;
    const context =
      typeof body === 'object' && body !== null && 'context' in body
        ? (body.context as Record<string, unknown>)
        : undefined;
    throw new ApiError(response.status, detail, code, context);
  }

  return (await response.json()) as T;
}

export async function apiPost<T>(
  path: string,
  body?: unknown,
  signal?: AbortSignal,
): Promise<T> {
  let response: Response;
  try {
    const init: RequestInit = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      ...(signal ? { signal } : {}),
    };
    response = await fetch(buildUrl(path), init);
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw new ApiError(0, 'Could not reach the server. Is it running?');
  }

  if (!response.ok) {
    const data: unknown = await response.json().catch(() => ({}));
    const detail =
      typeof data === 'object' && data !== null && 'detail' in data
        ? String(data.detail)
        : `Request failed (${response.status})`;
    const code =
      typeof data === 'object' && data !== null && 'code' in data
        ? String(data.code)
        : undefined;
    const context =
      typeof data === 'object' && data !== null && 'context' in data
        ? (data.context as Record<string, unknown>)
        : undefined;
    throw new ApiError(response.status, detail, code, context);
  }

  return (await response.json()) as T;
}

/** Response type of a GET endpoint, read straight from the generated schema. */
export type GetResponse<P extends keyof paths> = paths[P] extends {
  get: { responses: { 200: { content: { 'application/json': infer R } } } };
}
  ? R
  : never;
